// src/components/tesoreria/MovimientosChequesTesoreria.js
import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { Table, Container, Button, Form, Row, Col, Alert, Spinner, Badge } from "react-bootstrap";
import Contexts from "../../context/Contexts";
import NuevoMovimientoCheques from "./NuevoMovimientoCheques";
import OrdenPagoDetalleModal from "../comprasTesoreria/OrdenPagoDetalleModal.js";
import EditarEcheqModal from "./EditarEcheqModal";

const apiUrl = process.env.REACT_APP_API_URL;

export default function MovimientosChequesTesoreria() {
  const dataContext = useContext(Contexts.DataContext);
  const {
    empresaSeleccionada,
    bancosTabla = [],
    categoriasEgresoTabla = [],
    categoriasEgreso = [],
    proyectosTabla = [],
    proveedoresTabla = [],
  } = dataContext || {};

  // Proveedores (fallback si el contexto no los trae)
  const [proveedoresLocal, setProveedoresLocal] = useState([]);
  useEffect(() => {
    if ((proveedoresTabla || []).length > 0) return;
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${apiUrl}/proveedores`, { credentials: "include" });
        const data = await r.json();
        if (!cancel && Array.isArray(data)) setProveedoresLocal(data);
      } catch (e) {
        console.warn("No se pudo cargar proveedores:", e?.message);
      }
    })();
    return () => { cancel = true; };
  }, [proveedoresTabla]);

  const empresa_id = empresaSeleccionada?.id || null;

  // cache de OP (por id)
  const [ordenesCache, setOrdenesCache] = useState({}); // { [id]: orden }

  // Listado
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [bancoIdFiltro, setBancoIdFiltro] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState(""); // emitido|entregado|presentado|acreditado|rechazado|anulado
  const [porFecha, setPorFecha] = useState("emision");  // emision|vencimiento
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [numeroFiltro, setNumeroFiltro] = useState("");
  const [
    proveedorFiltro,
    setProveedorFiltro,
  ] = useState("");
  // Modales
  const [showNuevo, setShowNuevo] = useState(false);
  const [showDetalleOP, setShowDetalleOP] = useState(false);
  const [movSeleccionado, setMovSeleccionado] = useState(null);

  // Edición eCheq
  const [showEditarEcheq, setShowEditarEcheq] = useState(false);
  const [echeqEditar, setEcheqEditar] = useState(null);
  const [ordenTabla, setOrdenTabla] = useState({
    campo: "emision",
    direccion: "asc",
  });

  // Cat/Proy helpers
  const categorias = useMemo(
    () => (categoriasEgresoTabla?.length ? categoriasEgresoTabla : categoriasEgreso) || [],
    [categoriasEgresoTabla, categoriasEgreso]
  );
  const catById = useMemo(() => {
    const m = new Map();
    categorias.forEach((c) => m.set(Number(c.id), c));
    return m;
  }, [categorias]);
  const proyById = useMemo(() => {
    const m = new Map();
    (proyectosTabla || []).forEach((p) => m.set(Number(p.id), p));
    return m;
  }, [proyectosTabla]);

  // Proveedores
  const provById = useMemo(() => {
    const m = new Map();
    const fuente = (proveedoresTabla && proveedoresTabla.length > 0) ? proveedoresTabla : proveedoresLocal;
    (fuente || []).forEach((p) => m.set(Number(p.id), p));
    return m;
  }, [proveedoresTabla, proveedoresLocal]);

  const nombreProveedor = (id) => {
    const p = provById.get(Number(id));
    return p?.nombre || p?.razonsocial || (id ? `Proveedor #${id}` : "");
  };
  const nombreCategoria = (id) => catById.get(Number(id))?.nombre || "";
  const nombreProyecto = (id) => {
    const p = proyById.get(Number(id));
    return p?.descripcion || p?.nombre || "";
  };

  const bancosEmpresa = useMemo(() => {
    if (!empresa_id) return bancosTabla;
    return (bancosTabla || []).filter((b) => Number(b.empresa_id) === Number(empresa_id));
  }, [bancosTabla, empresa_id]);

  const fmtMoney = (n) =>
    `$${Number(n || 0).toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatFechaAR = (fecha) => {
    if (!fecha) return "";

    const soloFecha =
      String(fecha).split("T")[0];

    const partes =
      soloFecha.split("-");

    if (partes.length !== 3) {
      return fecha;
    }

    const [anio, mes, dia] = partes;

    return `${dia}/${mes}/${anio}`;
  };


  const buildQS = (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v) !== "") qs.set(k, v);
    });
    return qs.toString();
  };

  const loadItems = useCallback(async (overrides = {}) => {
    try {
      setLoading(true);
      const qs = buildQS({
        empresa_id: empresa_id || "",
        banco_id: (overrides.bancoIdFiltro ?? bancoIdFiltro) || "",
        estado: (overrides.estadoFiltro ?? estadoFiltro) || "",
        por: (overrides.porFecha ?? porFecha) || "emision",
        fecha_desde: (overrides.fechaDesde ?? fechaDesde) || "",
        fecha_hasta: (overrides.fechaHasta ?? fechaHasta) || "",
        numero_echeq: (overrides.numeroFiltro ?? numeroFiltro) || "",
        includeAnulados: "0",
      });
      const url = `${apiUrl}/echeqs-emitidos?${qs}`;
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      list.sort((a, b) => {
        const fa = (porFecha === "vencimiento" ? a.fecha_vencimiento : a.fecha_emision) || "";
        const fb = (porFecha === "vencimiento" ? b.fecha_vencimiento : b.fecha_emision) || "";
        if (fa === fb) return a.id - b.id;
        return fa.localeCompare(fb);
      });
      setItems(list);

      // Prefetch OP
      const ordenIds = [...new Set(list.map((m) => m.ordenpago_id).filter(Boolean))];
      const faltantes = ordenIds.filter((id) => !ordenesCache[id]);
      if (faltantes.length) {
        const fetched = await Promise.all(
          faltantes.map(async (id) => {
            try {
              const r = await fetch(`${apiUrl}/ordenes-pago/${id}`, { credentials: "include" });
              const j = await r.json();
              if (!r.ok) throw new Error(j?.error || `No se pudo obtener OP #${id}`);
              return [id, j];
            } catch (e) {
              console.warn("⚠️ fetch OP", id, e.message);
              return [id, null];
            }
          })
        );
        const asObj = Object.fromEntries(fetched.filter(([_, v]) => !!v));
        if (Object.keys(asObj).length) setOrdenesCache((prev) => ({ ...prev, ...asObj }));
      }
    } catch (err) {
      console.error("❌ Error al cargar eCheqs emitidos:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [empresa_id, bancoIdFiltro, estadoFiltro, porFecha, fechaDesde, fechaHasta, numeroFiltro, ordenesCache]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const onAplicarFiltro = () => loadItems();
  const onLimpiarFiltro = () => {
    setProveedorFiltro("");
    setBancoIdFiltro("");
    setEstadoFiltro("");
    setPorFecha("emision");
    setFechaDesde("");
    setFechaHasta("");
    setNumeroFiltro("");
    loadItems({
      bancoIdFiltro: "",
      estadoFiltro: "",
      porFecha: "emision",
      fechaDesde: "",
      fechaHasta: "",
      numeroFiltro: "",
    });
  };

  const bancoNombre = (id) => {
    const b = (bancosTabla || []).find((x) => Number(x.id) === Number(id));
    return b?.nombre || b?.descripcion || b?.alias || (id ? `Banco #${id}` : "");
  };

  const colorEstado = (estado) => {
    switch ((estado || "").toLowerCase()) {
      case "emitido": return "secondary";
      case "entregado": return "info";
      case "presentado": return "warning";
      case "acreditado": return "success";
      case "rechazado": return "danger";
      case "anulado": return "dark";
      default: return "secondary";
    }
  };

  // Acciones
  const doAccion = async (id, accion, body = {}) => {
    try {
      const res = await fetch(`${apiUrl}/echeqs-emitidos/${id}/${accion}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body || {}),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `No se pudo ${accion} eCheq`);
      loadItems();
    } catch (e) {
      alert(e.message);
    }
  };

  const doEliminar = async (id) => {
    if (!window.confirm("¿Eliminar definitivamente este eCheq? Se revertirán abonos/aplicaciones y OP vinculada.")) return;
    try {
      const res = await fetch(`${apiUrl}/echeqs-emitidos/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || "No se pudo eliminar el eCheq");
      loadItems();
    } catch (e) {
      alert(e.message);
    }
  };

  const onRowDblClick = (m) => {
    if (!m?.ordenpago_id) return;
    setMovSeleccionado(m);
    setShowDetalleOP(true);

    if (!ordenesCache[m.ordenpago_id]) {
      (async () => {
        try {
          const r = await fetch(`${apiUrl}/ordenes-pago/${m.ordenpago_id}`, { credentials: "include" });
          const j = await r.json();
          if (r.ok) setOrdenesCache(prev => ({ ...prev, [m.ordenpago_id]: j }));
        } catch (e) {
          console.warn("⚠️ No se pudo pre-cargar OP:", e?.message);
        }
      })();
    }
  };

  const itemsFiltrados = useMemo(() => {

    if (!proveedorFiltro) {
      return items;
    }


    const proveedorId =
      Number(proveedorFiltro);


    return items.filter(
      (m) =>
        Number(m.proveedor_id) ===
        proveedorId
    );

  }, [
    items,
    proveedorFiltro,
  ]);

  const cambiarOrden = (campo) => {

    setOrdenTabla((prev) => {

      if (prev.campo === campo) {

        return {
          campo,
          direccion:
            prev.direccion === "asc"
              ? "desc"
              : "asc",
        };
      }

      return {
        campo,
        direccion: "asc",
      };
    });
  };


  const indicadorOrden = (campo) => {

    if (ordenTabla.campo !== campo) {
      return "";
    }

    return ordenTabla.direccion === "asc"
      ? " ▲"
      : " ▼";
  };


  const itemsOrdenados = useMemo(() => {

    const lista =
      [...itemsFiltrados];


    const obtenerValor = (m) => {

      switch (ordenTabla.campo) {

        case "id":
          return Number(m.id || 0);

        case "emision":
          return String(
            m.fecha_emision || ""
          );

        case "vencimiento":
          return String(
            m.fecha_vencimiento || ""
          );

        case "banco":
          return String(
            bancoNombre(m.banco_id) || ""
          );

        case "numero":
          return String(
            m.numero_echeq || ""
          );

        case "proveedor":
          return String(
            nombreProveedor(m.proveedor_id) || ""
          );

        case "proyecto":
          return String(
            nombreProyecto(m.proyecto_id) || ""
          );

        case "categoria":
          return String(
            nombreCategoria(m.categoriaegreso_id) || ""
          );

        case "estado":
          return String(
            m.estado || ""
          );

        case "importe":
          return Number(
            m.importe || 0
          );

        default:
          return "";
      }
    };


    lista.sort((a, b) => {

      const valorA =
        obtenerValor(a);

      const valorB =
        obtenerValor(b);

      let resultado = 0;


      if (
        typeof valorA === "number" &&
        typeof valorB === "number"
      ) {

        resultado =
          valorA - valorB;

      } else {

        resultado =
          String(valorA).localeCompare(
            String(valorB),
            "es",
            {
              numeric: true,
              sensitivity: "base",
            }
          );
      }


      /*
       * Desempate por ID.
       */
      if (resultado === 0) {

        resultado =
          Number(a.id || 0) -
          Number(b.id || 0);
      }


      return ordenTabla.direccion === "asc"
        ? resultado
        : -resultado;

    });


    return lista;

  }, [
    itemsFiltrados,
    ordenTabla,
    bancosTabla,
    provById,
    proyById,
    catById,
  ]);


  const { totalImporte } = useMemo(() => {

    const total =
      itemsFiltrados.reduce(
        (a, b) =>
          a + Number(b.importe || 0),
        0
      );


    return {
      totalImporte: total,
    };

  }, [
    itemsFiltrados,
  ]);

  const onNuevoCreated = () => {
    loadItems();
    setShowNuevo(false);
  };

  const handleEditarEcheq = (echeq) => {
    setEcheqEditar(echeq);
    setShowEditarEcheq(true);
  };

  return (
    <Container>
      <h1 className="my-list-title dark-text">eCheqs Emitidos</h1>

      <div className="mb-3">
        <Button
          className="mx-2"
          variant="success"
          onClick={() => setShowNuevo(true)}
          disabled={!empresa_id}
        >
          Nuevo eCheq
        </Button>
      </div>

      {!empresa_id && (
        <Alert variant="info" className="py-2">
          No hay empresa seleccionada: se listan <strong>todos</strong> los eCheqs. Para emitir uno nuevo,
          seleccioná una empresa (solo se podrán usar bancos de esa empresa).
        </Alert>
      )}

      {/* Filtros */}
      <Form className="mb-3">
        <Row className="g-2">

          <Col md={3}>

            <Form.Label>
              Proveedor
            </Form.Label>

            <Form.Select
              value={proveedorFiltro}
              onChange={
                (e) =>
                  setProveedorFiltro(
                    e.target.value
                  )
              }
              disabled={loading}
              className="form-control my-input"
            >

              <option value="">
                Todos
              </option>

              {Array.from(
                provById.values()
              )
                .sort(
                  (a, b) =>
                    String(
                      a.razonsocial ||
                      a.nombre ||
                      ""
                    ).localeCompare(
                      String(
                        b.razonsocial ||
                        b.nombre ||
                        ""
                      ),
                      "es"
                    )
                )
                .map(
                  (p) => (

                    <option
                      key={p.id}
                      value={p.id}
                    >
                      {
                        p.razonsocial ||
                        p.nombre ||
                        `Proveedor #${p.id}`
                      }
                    </option>

                  )
                )}

            </Form.Select>

          </Col>

          <Col md={3}>
            <Form.Label>Banco</Form.Label>
            <Form.Select
              value={bancoIdFiltro}
              onChange={(e) => setBancoIdFiltro(e.target.value)}
              disabled={loading}
              className="form-control my-input"
            >
              <option value="">Todos</option>
              {(bancosEmpresa || []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.nombre || b.descripcion || b.alias || `Banco ${b.id}`}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Estado</Form.Label>
            <Form.Select
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
              disabled={loading}
              className="form-control my-input"
            >
              <option value="">Todos</option>
              <option value="emitido">Emitido</option>
              <option value="entregado">Entregado</option>
              <option value="presentado">Presentado</option>
              <option value="acreditado">Acreditado</option>
              <option value="rechazado">Rechazado</option>
              <option value="anulado">Anulado</option>
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Fecha por</Form.Label>
            <Form.Select
              value={porFecha}
              onChange={(e) => setPorFecha(e.target.value)}
              disabled={loading}
              className="form-control my-input"
            >
              <option value="emision">Emisión</option>
              <option value="vencimiento">Vencimiento</option>
            </Form.Select>
          </Col>

          <Col md={3}>
            <Form.Label>Número eCheq</Form.Label>
            <Form.Control
              placeholder="(contiene)"
              value={numeroFiltro}
              onChange={(e) => setNumeroFiltro(e.target.value)}
              disabled={loading}
            />
          </Col>
        </Row>

        <Row className="g-2 mt-1">
          <Col md={3}>
            <Form.Label>Fecha desde</Form.Label>
            <Form.Control
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              disabled={loading}
            />
          </Col>
          <Col md={3}>
            <Form.Label>Fecha hasta</Form.Label>
            <Form.Control
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              disabled={loading}
            />
          </Col>



          <Col md="auto" className="d-flex align-items-end">
            <Button
              className="mx-1"
              variant="outline-secondary"
              onClick={onAplicarFiltro}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Aplicar filtro"}
            </Button>
            <Button
              className="mx-1"
              variant="outline-dark"
              onClick={onLimpiarFiltro}
              disabled={loading}
            >
              Limpiar
            </Button>
          </Col>
        </Row>
      </Form>

      <Table striped bordered hover>
        <thead>
          <tr>

            <th
              onClick={() => cambiarOrden("id")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por número"
            >
              #{indicadorOrden("id")}
            </th>

            <th
              onClick={() => cambiarOrden("emision")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por fecha de emisión"
            >
              Emisión{indicadorOrden("emision")}
            </th>

            <th
              onClick={() => cambiarOrden("vencimiento")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por vencimiento"
            >
              Vencimiento{indicadorOrden("vencimiento")}
            </th>

            <th
              onClick={() => cambiarOrden("banco")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por banco"
            >
              Banco{indicadorOrden("banco")}
            </th>

            <th
              onClick={() => cambiarOrden("numero")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por número de eCheq"
            >
              Número{indicadorOrden("numero")}
            </th>

            <th
              onClick={() => cambiarOrden("proveedor")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por proveedor"
            >
              Proveedor{indicadorOrden("proveedor")}
            </th>

            <th
              onClick={() => cambiarOrden("proyecto")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por proyecto"
            >
              Proyecto{indicadorOrden("proyecto")}
            </th>

            <th
              onClick={() => cambiarOrden("categoria")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por categoría"
            >
              Categoría{indicadorOrden("categoria")}
            </th>

            <th
              onClick={() => cambiarOrden("estado")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por estado"
            >
              Estado{indicadorOrden("estado")}
            </th>

            <th
              className="text-end"
              onClick={() => cambiarOrden("importe")}
              style={{ cursor: "pointer", userSelect: "none" }}
              title="Ordenar por importe"
            >
              Importe{indicadorOrden("importe")}
            </th>

            <th>
              Acciones
            </th>

          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={11} className="text-center text-muted">
                Cargando…
              </td>
            </tr>
          )}

          {!loading && itemsOrdenados.map((m) => (
            <tr
              key={m.id}
              onDoubleClick={() => onRowDblClick(m)}
              onClick={() => setMovSeleccionado(m)}
              style={{ cursor: m.ordenpago_id ? "pointer" : "default" }}
              title={m.ordenpago_id ? "Doble click para ver detalle de la Orden de Pago" : ""}
            >
              <td>{m.id}</td>
              <td>{formatFechaAR(m.fecha_emision)}</td>
              <td>{formatFechaAR(m.fecha_vencimiento)}</td>
              <td>{bancoNombre(m.banco_id)}</td>
              <td>{m.numero_echeq || ""}</td>
              <td>{nombreProveedor(m.proveedor_id)}</td>
              <td>{nombreProyecto(m.proyecto_id)}</td>
              <td>{nombreCategoria(m.categoriaegreso_id)}</td>
              <td>
                <Badge bg={colorEstado(m.estado)}>{m.estado}</Badge>
                {m.anulado ? <span className="ms-1 badge bg-dark">anulado</span> : null}
              </td>
              <td className="text-end">{fmtMoney(m.importe)}</td>

              <td className="text-nowrap">

                {/* Acciones según estado */}

                {!m.anulado && m.estado !== "acreditado" && (

                  <>

                    <Button
                      size="sm"
                      variant="outline-success"
                      className="mx-1"
                      onClick={(e) => {

                        e.stopPropagation();

                        const def =
                          new Date()
                            .toISOString()
                            .slice(0, 10);

                        const fecha_acreditacion =
                          prompt(
                            "Fecha de acreditación (YYYY-MM-DD):",
                            def
                          ) || "";

                        if (!fecha_acreditacion) {
                          return;
                        }

                        doAccion(
                          m.id,
                          "acreditar",
                          {
                            fecha_acreditacion,
                          }
                        );
                      }}
                    >
                      Acreditar
                    </Button>


                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="mx-1"
                      onClick={(e) => {

                        e.stopPropagation();

                        handleEditarEcheq(
                          m
                        );
                      }}
                    >
                      Editar
                    </Button>


                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="mx-1"
                      onClick={(e) => {

                        e.stopPropagation();

                        doEliminar(
                          m.id
                        );
                      }}
                    >
                      Eliminar
                    </Button>

                  </>

                )}


                {(m.anulado || m.estado === "acreditado") && (

                  <span className="text-muted small">
                    Sin acciones
                  </span>

                )}

              </td>

            </tr>
          ))}

          {!loading && itemsFiltrados.length === 0 && (
            <tr>
              <td colSpan={11} className="text-center text-muted">
                No hay eCheqs para mostrar.
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={9} className="text-end"><strong>Total</strong></td>
            <td className="text-end"><strong>{fmtMoney(totalImporte)}</strong></td>
            <td />
          </tr>
        </tfoot>
      </Table>

      {/* Alta */}
      <NuevoMovimientoCheques
        show={showNuevo}
        onHide={() => setShowNuevo(false)}
        onCreated={onNuevoCreated}
      />

      {/* Editar eCheq */}
      <EditarEcheqModal
        show={showEditarEcheq}
        row={echeqEditar}
        onHide={() => {
          setShowEditarEcheq(false);
          setEcheqEditar(null);
        }}
        onUpdated={async () => {
          setShowEditarEcheq(false);
          setEcheqEditar(null);

          await loadItems();
        }}
      />

      {/* Detalle OP (si existe) */}
      <OrdenPagoDetalleModal
        show={showDetalleOP}
        onHide={() => setShowDetalleOP(false)}
        movimiento={movSeleccionado}
        ordenId={movSeleccionado?.ordenpago_id ? Number(movSeleccionado.ordenpago_id) : undefined}
        orden={movSeleccionado?.ordenpago_id ? ordenesCache[movSeleccionado.ordenpago_id] : null}
      />
    </Container>
  );
}
